import { findGroupsByCartId } from "../lib/cartGroupApi";
import { removeCartItem, updateCartItem } from "../lib/cartGroupItemApis";

import {
  updateCartItemQuantity,
  setCart,
  updateCartVersion,
} from "../redux/cartReducer";
import { Dispatch, AnyAction } from "redux";
import {
  registerItemUpdate,
  registerFeeCalculation,
} from "../redux/cartOperationsAdapter";

// Update CartItem type definition
interface CartItem {
  cartCustomId: string;
  quantity: number;
  groupId?: string;
  group?: {
    id?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Handles cart item quantity updates with optimistic UI updates
 * Uses CartOperationManager for centralized operations
 */
export const handleCartItemUpdate = async (
  newQuantity: number,
  item: CartItem,
  dispatch: Dispatch<AnyAction>
): Promise<void> => {
  const updateId = Date.now();
  console.log(`[DEBUG-UTILS] handleCartItemUpdate #${updateId} started`, {
    cartCustomId: item.cartCustomId,
    newQuantity,
    groupId: item.groupId,
  });

  const startTime = Date.now();

  const groupId =
    item.groupId ||
    (item.group && typeof item.group === "object" ? item.group?.id : undefined);

  if (!groupId) {
    console.error("No group ID found for item", item);
    return;
  }

  // Register the item update operation through the adapter
  const itemOperation = registerItemUpdate(
    dispatch,
    groupId,
    item.cartCustomId,
    newQuantity
  );

  try {
    // Optimistic update
    dispatch(
      updateCartItemQuantity({
        cartCustomId: item.cartCustomId,
        quantity: newQuantity,
      })
    );

    let response;
    if (newQuantity <= 0) {
      response = await removeCartItem(item.cartCustomId);
    } else {
      response = await updateCartItem(item.cartCustomId, {
        quantity: newQuantity,
        manualUpdate: true,
        recalculateWeight: true,
        recalculateFee: true, // Backend will handle fee recalculation
      });
    }

    // Update cart with the response
    if (response?.cart) {
      dispatch(setCart(response.cart));
    } else {
      // Register fee calculation operation if cart wasn't returned
      const feeOperation = registerFeeCalculation(dispatch, groupId);

      try {
        const cartId = localStorage.getItem("cartId");
        if (cartId) {
          const cartResponse = await findGroupsByCartId(cartId);
          if (cartResponse?.cart) {
            dispatch(setCart(cartResponse.cart));
          }
        }
        feeOperation.complete(true);
      } catch (feeError) {
        console.error("Failed to update fees:", feeError);
        feeOperation.complete(false);
      }
    }

    if (response?.cartVersion) {
      dispatch(updateCartVersion(response.cartVersion));
    }

    // Complete the item update operation
    itemOperation.complete(true);


  } catch (error) {
    console.error(`❌ Failed to update cart item:`, error);

    // Mark operation as failed
    itemOperation.complete(false);

    // Try to refresh the cart to ensure consistent state
    try {
      const cartId = localStorage.getItem("cartId");
      if (cartId) {
        const response = await findGroupsByCartId(cartId);
        if (response?.cart) {
          dispatch(setCart(response.cart));
        }
      }
    } catch (refreshError) {
      console.error("❌ Failed to refresh cart after error:", refreshError);
    }
  }
};
